/**
 * BaseField Component - Common wrapper for all form fields
 * Provides consistent styling, labels, errors, and help text
 */

import React from 'react';
import { AlertCircle, Info } from 'lucide-react';
import { BaseFieldProps } from '../../../types/forms';

interface ExtendedBaseFieldProps extends BaseFieldProps {
  children: React.ReactNode;
  fieldId?: string;
}

export const BaseField: React.FC<ExtendedBaseFieldProps> = ({
  name,
  label,
  helpText,
  required = false,
  disabled = false,
  readonly = false,
  error,
  className = '',
  children,
  fieldId,
}) => {
  const id = fieldId || `field-${name}`;

  return (
    <div className={`form-field ${className}`}>
      {label && (
        <label
          htmlFor={id}
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
          {readonly && (
            <span className="ml-2 text-xs text-gray-500">(Read only)</span>
          )}
        </label>
      )}

      <div className={`relative ${disabled ? 'opacity-50' : ''}`}>
        {children}
      </div>

      {helpText && !error && (
        <div className="mt-1 flex items-start text-sm text-gray-600">
          <Info className="w-4 h-4 mr-1 mt-0.5 flex-shrink-0" />
          <span>{helpText}</span>
        </div>
      )}

      {error && (
        <div className="mt-1 flex items-start text-sm text-red-600">
          <AlertCircle className="w-4 h-4 mr-1 mt-0.5 flex-shrink-0" />
          <span role="alert">{error}</span>
        </div>
      )}
    </div>
  );
};

// Higher-order component to wrap field components with BaseField
export function withBaseField<P extends BaseFieldProps>(
  FieldComponent: React.ComponentType<P>
): React.FC<P> {
  return (props: P) => {
    const { label, helpText, required, disabled, readonly, error, className, ...fieldProps } = props;

    return (
      <BaseField
        name={props.name}
        label={label}
        helpText={helpText}
        required={required}
        disabled={disabled}
        readonly={readonly}
        error={error}
        className={className}
      >
        <FieldComponent
          {...(fieldProps as P)}
          disabled={disabled}
          readonly={readonly}
        />
      </BaseField>
    );
  };
}

export default BaseField;